import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { Document, Page } from 'react-pdf';
import { useDropzone } from 'react-dropzone';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { ADD_FILE_FOR_TALENT, ADD_FILE_FOR_TALENT_UPERCUT, DELETE_FILES, DELETE_FILES_FOR_TALENT_UPERCUT, DOWNLOAD_FILE } from '@gql/file';
import usePdfWorker from '@modules/cv-generator/use-pdf-worker';
import { isPdfFile } from '@utils/fileHelpers';

interface ResumeViewerProps {
    resumeUrl: string | null | undefined;
    fileId: string | null | undefined;
    talentId: string | undefined;
    onUploadSuccess: () => void;
}

const ResumeViewer = ({ resumeUrl, fileId, talentId, onUploadSuccess }: ResumeViewerProps) => {
    const theme = useTheme();
    const workerReady = usePdfWorker();
    const isPdf = isPdfFile(undefined, resumeUrl ?? undefined);
    const [numPages, setNumPages] = useState<number>(0);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [blobLoading, setBlobLoading] = useState(false);

    const { isUpercut } = useAuth();
    const { isProIdRequired } = useProId();
    const isUpercutFlow = isUpercut && !isProIdRequired;

    const [addFile] = useMutation(isUpercutFlow ? ADD_FILE_FOR_TALENT_UPERCUT : ADD_FILE_FOR_TALENT);
    const [deleteFiles] = useMutation(isUpercutFlow ? DELETE_FILES_FOR_TALENT_UPERCUT : DELETE_FILES);

    const [fetchSignedUrl] = useLazyQuery(DOWNLOAD_FILE, {
        onCompleted: data => {
            if (data?.downloadFile?.url) {
                setSignedUrl(data.downloadFile.url);
            }
        },
        onError: () => {
            // Fall back to the raw URL if signing fails
            setSignedUrl(resumeUrl ?? null);
        },
    });

    // Fetch the PDF as a blob to avoid CORS issues with react-pdf (PDF only)
    useEffect(() => {
        if (!signedUrl || !isPdf) {
            setBlobUrl(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            return;
        }
        let cancelled = false;
        setBlobLoading(true);
        setPdfError(null);
        fetch(signedUrl)
            .then(res => {
                if (!res.ok) throw new Error('fetch failed');
                return res.blob();
            })
            .then(blob => {
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                setBlobUrl(prev => {
                    if (prev) URL.revokeObjectURL(prev);
                    return url;
                });
            })
            .catch(() => {
                if (!cancelled) setPdfError('Impossible de charger le fichier PDF.');
            })
            .finally(() => {
                if (!cancelled) setBlobLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [signedUrl, isPdf]);

    useEffect(() => {
        // Reset state whenever the source changes
        setPdfError(null);
        setNumPages(0);

        if (!resumeUrl) {
            setSignedUrl(null);
            return;
        }
        // Cloudinary URLs are public — use directly
        if (resumeUrl.includes('cloudinary')) {
            setSignedUrl(resumeUrl);
            return;
        }
        if (fileId) {
            fetchSignedUrl({ variables: { _id: fileId } });
        } else {
            // No id available, try the raw URL as a last resort
            setSignedUrl(resumeUrl);
        }
    }, [resumeUrl, fileId]);

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];
            if (!file || !talentId) return;
            setUploading(true);
            try {
                if (fileId) {
                    await deleteFiles({
                        variables: {
                            _ids: [fileId],
                            ...(isUpercutFlow ? { talentId } : {}),
                        },
                    });
                }
                await addFile({ variables: { file, talentId, context: 'resume' } });
                onUploadSuccess();
            } finally {
                setUploading(false);
            }
        },
        [addFile, deleteFiles, fileId, isUpercutFlow, talentId, onUploadSuccess],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        maxFiles: 1,
        disabled: uploading || !talentId,
    });

    if (!signedUrl) {
        return (
            <Box
                sx={{
                    border: `2px dashed ${theme.palette.border.main}`,
                    borderRadius: 2,
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    minHeight: 320,
                    bgcolor: isDragActive ? theme.palette.transparentPurple.main : 'transparent',
                    transition: 'background-color 0.2s',
                }}
                {...getRootProps()}
            >
                <input {...getInputProps()} />
                {uploading ? (
                    <CircularProgress size={40} />
                ) : (
                    <>
                        <i className="fi fi-br-file-pdf" style={{ fontSize: 48, color: theme.palette.text.secondary }} />
                        <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                            Aucun CV disponible
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
                            {isDragActive
                                ? 'Déposez le CV ici...'
                                : 'Glissez-déposez le CV PDF ou cliquez pour sélectionner un fichier'}
                        </Typography>
                        <Button variant="contained" size="small" disabled={!talentId}>
                            Importer le CV
                        </Button>
                    </>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                    Résumé du candidat
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        variant="contained"
                        href={signedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<i className="fi fi-br-download" style={{ fontSize: 14 }} />}
                    >
                        Télécharger
                    </Button>
                    {/* Allow replacing the resume via drag-drop */}
                    <Box component="label" sx={{ cursor: 'pointer' }}>
                        <input
                            type="file"
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file || !talentId) return;
                                setUploading(true);
                                try {
                                    if (fileId) {
                                        await deleteFiles({
                                            variables: {
                                                _ids: [fileId],
                                                ...(isUpercutFlow ? { talentId } : {}),
                                            },
                                        });
                                    }
                                    await addFile({ variables: { file, talentId, context: 'resume' } });
                                    onUploadSuccess();
                                } finally {
                                    setUploading(false);
                                }
                            }}
                        />
                        <Button
                            size="small"
                            variant="contained"
                            component="span"
                            disabled={uploading || !talentId}
                            startIcon={
                                uploading
                                    ? <CircularProgress size={12} />
                                    : <i className="fi fi-br-upload" style={{ fontSize: 14 }} />
                            }
                        >
                            Remplacer
                        </Button>
                    </Box>
                </Box>
            </Box>

            {isPdf ? (
                pdfError ? (
                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.border.main}`,
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: theme.palette.errorRed.main, mb: 1 }}>
                            Impossible d'afficher le PDF
                        </Typography>
                        <Button size="small" variant="contained" href={signedUrl ?? undefined} target="_blank" rel="noopener noreferrer">
                            Ouvrir dans un nouvel onglet
                        </Button>
                    </Box>
                ) : !workerReady || blobLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.border.main}`,
                            borderRadius: 2,
                            overflow: 'auto',
                            maxHeight: 'calc(100vh - 430px)',
                            bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 2,
                            gap: 1,
                        }}
                    >
                        <Document
                            file={blobUrl}
                            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                            onLoadError={() => setPdfError('Impossible de charger le fichier PDF.')}
                            loading={
                                <Box sx={{ py: 8 }}>
                                    <CircularProgress />
                                </Box>
                            }
                        >
                            {Array.from({ length: numPages }, (_, i) => (
                                <Box key={i + 1} sx={{ mb: 1, boxShadow: 2 }}>
                                    <Page
                                        pageNumber={i + 1}
                                        width={Math.min(700, window.innerWidth - 80)}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                    />
                                </Box>
                            ))}
                        </Document>
                        {numPages > 1 && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                                {numPages} page{numPages > 1 ? 's' : ''}
                            </Typography>
                        )}
                    </Box>
                )
            ) : (
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 2,
                        overflow: 'auto',
                        maxHeight: 'calc(100vh - 430px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                    }}
                >
                    <img
                        src={signedUrl ?? undefined}
                        alt="CV"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ResumeViewer;
